/**
 * Firebase Authentication Helper
 * Handles Firebase OAuth flow (Google Sign-In via Firebase SDK)
 * Keeps Firebase-specific logic isolated from main auth service
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider, 
  signOut as firebaseSignOut,
  type Auth,
  type User as FirebaseUser,
  type UserCredential
} from 'firebase/auth';
import { logger } from '../../utils/logger';

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

export interface FirebaseAuthResult {
  idToken: string;
  firebaseUser: FirebaseUser;
}

/**
 * Firebase Authentication Helper Class
 * Manages Firebase initialization and Google OAuth flow
 */
export class FirebaseAuthHelper {
  private firebaseApp: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private googleProvider: GoogleAuthProvider;
  private initialized: boolean = false;

  constructor() {
    this.googleProvider = new GoogleAuthProvider();
    this.googleProvider.addScope('email');
    this.googleProvider.addScope('profile');
  }

  /**
   * Initialize Firebase with configuration
   */
  initialize(config: FirebaseConfig): void {
    if (this.initialized) {
      logger.child('FIREBASE').warn('Firebase already initialized');
      return;
    }

    try {
      this.firebaseApp = initializeApp(config);
      this.auth = getAuth(this.firebaseApp);
      this.initialized = true;
      logger.child('FIREBASE').log('Firebase initialized successfully');
    } catch (error) {
      logger.child('FIREBASE').error('Firebase initialization failed:', error);
      throw new Error('Failed to initialize Firebase');
    }
  }

  /**
   * Check if Firebase is initialized
   */
  isInitialized(): boolean {
    return this.initialized && this.auth !== null;
  }

  /**
   * Sign in with Google using Firebase
   * Tries popup first, falls back to redirect if blocked
   */
  async signInWithGoogle(): Promise<FirebaseAuthResult> {
    if (!this.isInitialized() || !this.auth) {
      throw new Error('Firebase not initialized. Call initialize() first.');
    }

    try {
      // Configure provider for redirect fallback
      this.googleProvider.setCustomParameters({
        prompt: 'select_account'
      });

      // Check if we're returning from a redirect
      const redirectResult = await getRedirectResult(this.auth);
      
      let result: UserCredential;
      
      if (redirectResult) {
        // User returned from redirect
        result = redirectResult;
      } else {
        // Try popup first
        try {
          result = await signInWithPopup(this.auth, this.googleProvider);
        } catch (popupError: any) {
          // Check if popup was blocked or COOP issues
          if (
            popupError.code === 'auth/popup-blocked' || 
            popupError.message?.includes('Cross-Origin-Opener-Policy') ||
            popupError.message?.includes('window.closed')
          ) {
            // Fallback to redirect
            logger.child('FIREBASE').warn('Popup blocked, falling back to redirect');
            await signInWithRedirect(this.auth, this.googleProvider);
            // Redirect will happen, return empty result
            throw new Error('REDIRECT_IN_PROGRESS');
          }
          throw popupError;
        }
      }

      // Get Firebase ID token
      const idToken = await result.user.getIdToken();

      return {
        idToken,
        firebaseUser: result.user
      };
    } catch (error: any) {
      if (error.message === 'REDIRECT_IN_PROGRESS') {
        throw error;
      }
      logger.child('FIREBASE').error('Firebase Google sign-in failed:', error);
      throw new Error(`Firebase authentication failed: ${error.message}`);
    }
  }

  /**
   * Sign out from Firebase
   */
  async signOut(): Promise<void> {
    if (!this.isInitialized() || !this.auth) {
      return;
    }

    try {
      await firebaseSignOut(this.auth);
    } catch (error) {
      logger.child('FIREBASE').error('Firebase sign-out failed:', error);
      throw error;
    }
  }

  /**
   * Get current Firebase user
   */
  getCurrentUser(): FirebaseUser | null {
    if (!this.isInitialized() || !this.auth) {
      return null;
    }
    return this.auth.currentUser;
  }

  /**
   * Clean up Firebase instance
   */
  destroy(): void {
    this.firebaseApp = null;
    this.auth = null;
    this.initialized = false;
  }
}

// Export singleton instance
export const firebaseAuthHelper = new FirebaseAuthHelper();
export default firebaseAuthHelper;
